/**
 * Offline read/write helpers.
 *
 * `navigator.onLine` cannot be trusted: after reloading a page while offline
 * under the service worker it reports `true`, so a pre-check alone silently
 * skips every offline fallback. So: keep the pre-check as a fast path for when
 * the browser is honest, but also treat a request failure as a signal to read
 * the cache.
 *
 * Only use these for cacheable reads and queueable writes. Do not use them for
 * anything transactional — a real server error would be masked by stale data.
 */

/**
 * Did this error come from a request that never reached the server?
 *
 * `err.request` is the discriminator. axios sets it for anything that actually
 * went out on the wire (`enhanceError`, called from `createError` in
 * `lib/adapters/xhr.js`: `onerror` passes the request with no response), so its
 * absence proves the error did not come from a request at all — a plain `Error`
 * thrown by post-success cache work, a `TypeError`, a Dexie `DataError`. Those
 * must not be treated as "server unreachable", because a caller that queues
 * them would replay a write the server has already applied.
 *
 * Deliberately NOT keyed on `isAxiosError`: this repo pins axios 0.18.1, where
 * that property does not exist (added in 0.19.0). A predicate using it would be
 * permanently false and would silently disable all offline queueing.
 *
 * The `__CANCEL__` and `ECONNABORTED` clauses are latent today — `xhrService`
 * sets no `timeout` and nothing in `src/` uses `CancelToken`, so axios cannot
 * currently produce either. They are here so that adding a request timeout
 * later does not quietly turn timeouts into queued duplicate writes.
 */
export const isNetworkFailure = (err: any) =>
  Boolean(err?.request) &&
  !err.response &&
  !err.__CANCEL__ &&
  err.code !== 'ECONNABORTED';

/**
 * Offline write helper.
 *
 * Same `navigator.onLine` problem as reads, but the stakes differ: a write that
 * takes the online branch and fails is simply lost, because the branch that
 * queues it to IndexedDB never runs.
 *
 * Only queues on network-shaped failures. A real server rejection (validation,
 * permission, conflict) must surface rather than be queued as though it
 * succeeded and replayed later.
 *
 * The `try` deliberately wraps only `request()`. Anything a caller does after a
 * successful request — mirroring the result into IndexedDB, say — must do its
 * own error handling, because a throw from there means the server has *already*
 * applied the write and queueing it would apply it twice.
 */
export async function withOfflineWrite<T>(
  request: () => Promise<T>,
  queue: () => Promise<T>,
  /**
   * `replay` turns queueing off for this call.
   *
   * Pass it when the call *is* the flush of an already-queued write. The sync
   * loop's shape is `try { await api.create(...); markSynced(); } catch {}`, so
   * a resolved promise means "the server has it". Queueing there would resolve
   * on an unreachable server, the caller would mark the row synced although the
   * server never received it, and the payload would be appended a second time —
   * the row is then dropped from the next sync pass and the write is lost.
   *
   * A replay must therefore fail the way it did before these helpers existed:
   * reject, and leave the row queued for the next attempt.
   *
   * ---
   *
   * `localOnlyWhenOffline` is for an operation whose "queue" is not actually
   * replayed by anything.
   *
   * The sync loop in InspectionsDue.jsx only branches on `method: 'create'` and
   * `method: 'update'`; no code writes or replays a delete, and the reopening
   * logs record no reopen operation at all. So for those the local write is
   * final: nothing will ever tell the server, and the next download restores
   * the server's version.
   *
   * Resolving after such a write claims "this will sync" when it never will, so
   * a request failure must surface instead. The `!navigator.onLine` fast path
   * above is left intact — that is the behaviour these operations already had
   * before this ticket, and narrowing it is a separate decision from fixing the
   * pre-check.
   *
   * Remove this flag from an operation once its replay support exists.
   * (Raised by Copilot on PR #53.)
   */
  options: { replay?: boolean; localOnlyWhenOffline?: boolean } = {},
): Promise<T> {
  if (options.replay) {
    return request();
  }

  if (!navigator.onLine) {
    return queue();
  }

  try {
    return await request();
  } catch (err) {
    if (isNetworkFailure(err) && !options.localOnlyWhenOffline) {
      return queue();
    }

    throw err;
  }
}

export async function withOfflineFallback<T>(
  request: () => Promise<T>,
  cached: () => Promise<T | null | undefined>,
  /**
   * `onlyWhenUnreachable` restricts the fallback to network-shaped failures.
   * Default (permissive) is right for reads that gate rendering: a 5xx should
   * still show cached data rather than an empty screen. Pass `true` for reads
   * whose result feeds a write, or where an empty/degraded result is
   * indistinguishable from a legitimate one and would mask a real server error.
   */
  options: { onlyWhenUnreachable?: boolean } = {},
): Promise<T> {
  const readCache = async () => {
    try {
      return await cached();
    } catch (err) {
      console.error('Offline cache read failed', err);

      return null;
    }
  };

  if (!navigator.onLine) {
    const hit = await readCache();

    // `!= null` rather than a truthiness check: an endpoint whose legitimate
    // value is `0`, `''` or `false` could otherwise never be served from cache.
    if (hit != null) {
      return hit;
    }
  }

  try {
    return await request();
  } catch (err) {
    // An authorization failure is a real answer from the server, not an outage.
    // Falling back here would let a revoked permission or an archived client
    // keep reading as success for as long as the cache survives.
    //
    // No `: any` on the catch binding — TypeScript is pinned at 3.9 here, which
    // rejects catch-clause type annotations (TS1196). Jest transforms via babel
    // and strips them, so only the production build catches it.
    const status = (err as any)?.response?.status;

    if (status === 401 || status === 403) {
      throw err;
    }

    if (options.onlyWhenUnreachable && !isNetworkFailure(err)) {
      throw err;
    }

    const hit = await readCache();

    if (hit != null) {
      return hit;
    }

    throw err;
  }
}
